import {
  CheckoutAttributesUpdateInput,
  CheckoutDiscountCodeApplyInput,
  CheckoutDiscountCodeRemoveInput,
  CheckoutEmailUpdateInput,
  CheckoutLineItemsAddInput,
  CheckoutLineItemsRemoveInput,
  CheckoutLineItemsUpdateInput,
  CreateCartInput,
  CartLinesAddInput,
  CartLinesUpdateInput,
  CartBuyerIdentityUpdateInput,
  CreateCheckoutInput,
  GetCollectionParam,
  MailingAddressInput,
  TokenizedPaymentInput,
} from '../../type'
import {
  CollectionQuery,
  CollectionAndProductByIdsQuery,
  CollectionAndProductByHandleQuery,
} from '../graphql/Collection.graphql'
import { PageQuery } from '../graphql/Page.graphql'
import {
  ArticleBlogByHandleQuery,
  ListArticleBlogByHandleQuery,
} from '../graphql/Blog.graphql'
import {
  ProductQuery,
  ProductQueryId,
  ProductRecommendations,
} from '../graphql/Product.graphql'
import {
  CartQuery,
  CreatCartMutation,
  CartLinesAddMutation,
  CartLinesUpdateMutation,
  CartBuyerIdentityUpdateMutation,
  CartDiscountCodesUpdateMutation,
  CartAttributesUpdateMutation,
} from '../graphql/Cart.graphql'
import {
  CreateCheckoutMutation,
  CheckoutQuery,
  CheckoutCompleteWithTokenizedPaymentV3,
  CheckoutShippingAddressUpdateV2,
  CheckoutShippingLineUpdate,
  CheckoutCompleteFree,
  CheckoutLineItemsAddMutation,
  CheckoutLineItemsRemoveMutation,
  CheckoutLineItemsUpdateMutation,
  CheckoutAttributesUpdateV2,
  CheckoutDiscountCodeApplyV2,
  CheckoutDiscountCodeRemove,
  CheckoutEmailUpdateV2,
} from '../graphql/Checkout.graphql'
import { UrlRedirectQuery } from '../graphql/OnlineStore.graphql'
import {
  CreateCustomerMutation,
  CreateCustomerAccessTokenMutation,
  CustomerQuery,
  CustomerRecoverMutation,
  CustomerResetMutation,
  CustomerActiveMutation,
  CreateCustomerAddressMutation,
  UpdateCustomerAddressMutation,
  LocalizationQuery,
} from '../graphql/Customer.graphql'
import {
  ApolloClient,
  HttpLink,
  ApolloLink,
  InMemoryCache,
  concat,
  NormalizedCacheObject,
  DocumentNode,
  DefaultOptions,
} from '@apollo/client/core/index.js'
import { isPrimitiveType, isObjectType, SHOP_ID } from '@/utils'
import fetch from 'cross-fetch'
import { getShopifyCookies } from '@/services/analytics/shopify/cookies-utils'
import { isOnServer } from '@/utils/ssr'
import {
  SHOPIFY_S,
  SHOPIFY_STOREFRONT_ID_HEADER,
  SHOPIFY_STOREFRONT_S_HEADER,
  SHOPIFY_STOREFRONT_Y_HEADER,
  SHOPIFY_Y,
} from '@/services/analytics/shopify/cart-constants'
import { HttpHeader } from '@/services/http'
import {
  CustomerAccessTokenCreateInput,
  CustomerActivateInput,
  CustomerCreateInput,
  CustomerResetInput,
} from '@/provider/type'

export class ShopifyGraphQLClient {
  private domain: string
  private storefrontAccessToken: string
  private apiVersion = '2022-01'
  private client: ApolloClient<NormalizedCacheObject>
  constructor({
    domain,
    storefrontAccessToken,
    apiVersion,
  }: {
    domain: string
    storefrontAccessToken: string
    apiVersion?: string
  }) {
    this.domain = domain
    this.storefrontAccessToken = storefrontAccessToken
    if (apiVersion) {
      this.apiVersion = apiVersion
    }
    const url = `https://${this.domain}/api/${this.apiVersion}/graphql.json`

    const httpLink = new HttpLink({ uri: url, fetch })

    const authMiddleware = new ApolloLink((operation, forward) => {
      const customHeaders: HttpHeader = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
        [SHOPIFY_STOREFRONT_ID_HEADER]: `gid://shopify/Shop/${SHOP_ID}`,
      }
      if (!isOnServer) {
        const cookies = getShopifyCookies(document.cookie)
        if (cookies[SHOPIFY_Y])
          customHeaders[SHOPIFY_STOREFRONT_Y_HEADER] = cookies[SHOPIFY_Y]
        if (cookies[SHOPIFY_S])
          customHeaders[SHOPIFY_STOREFRONT_S_HEADER] = cookies[SHOPIFY_S]
      }
      // add the authorization to the headers
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          ...customHeaders,
        },
      }))

      return forward(operation)
    })

    // Disable apollo client cache because we have another cache service that well controlled and do not consume RAM
    const defaultOptions: DefaultOptions = {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    }

    this.client = new ApolloClient({
      cache: new InMemoryCache(),
      link: concat(authMiddleware, httpLink),
      defaultOptions,
    })
  }
  async query(query: DocumentNode, variables?: any): Promise<any> {
    const response = await this.client.query({
      query,
      variables,
    })
    return cleanGraphQLResponse(response.data)
  }

  async mutate(mutation: DocumentNode, variables?: any): Promise<any> {
    const response = await this.client.mutate({
      mutation,
      variables,
    })
    return cleanGraphQLResponse(response.data)
  }

  getPageByHandle(handle: string): Promise<any> {
    return this.query(PageQuery, {
      handle,
    })
  }

  getArticleBlogByHandle(
    blogHandle: string,
    articleHandle: string
  ): Promise<any> {
    return this.query(ArticleBlogByHandleQuery, {
      blogHandle,
      articleHandle,
    })
  }

  getListArticleBlogByHandle(param: {
    handle: string
    numberOfArticles?: number
    after?: string
    before?: string
  }): Promise<any> {
    const query: any = {
      handle: param.handle,
      after: param.after,
      before: param.before,
    }
    if (param.before) {
      query.last = param.numberOfArticles
    } else {
      query.first = param.numberOfArticles
    }
    return this.query(ListArticleBlogByHandleQuery, query)
  }

  getCollectionWithProductsByHandle(param: GetCollectionParam): Promise<any> {
    const query: any = {
      handle: param.handle,
      after: param.after,
      before: param.before,
      reverse: param.reverse,
      sortKey: param.sortKey,
      filters: param.filters,
    }
    if (param.before) {
      query.last = param.numberOfProducts
    } else {
      query.first = param.numberOfProducts
    }
    return this.query(CollectionQuery, query)
  }

  getCollectionWithProductsByHandleAndProductByIds(
    param: GetCollectionParam
  ): Promise<any> {
    const query: any = {
      handle: param.handle,
      after: param.after,
      before: param.before,
      reverse: param.reverse,
      sortKey: param.sortKey,
      filters: param.filters,
      productIds: param.productIds,
    }
    if (param.before) {
      query.last = param.numberOfProducts
    } else {
      query.first = param.numberOfProducts
    }
    return this.query(CollectionAndProductByIdsQuery, query)
  }

  getCollectionWithProductsByHandleAndProductByHandle(
    param: GetCollectionParam
  ): Promise<any> {
    const query: any = {
      handle: param.handle,
      after: param.after,
      before: param.before,
      reverse: param.reverse,
      sortKey: param.sortKey,
      filters: param.filters,
      productHandle: param.productHandle,
    }
    if (param.before) {
      query.last = param.numberOfProducts
    } else {
      query.first = param.numberOfProducts
    }
    return this.query(CollectionAndProductByHandleQuery, query)
  }

  getProductByHandle(handle: string): Promise<any> {
    return this.query(ProductQuery, {
      handle,
    })
  }

  getProductById(id: string): Promise<any> {
    return this.query(ProductQueryId, {
      id,
    })
  }

  createCart(input: CreateCartInput): Promise<any> {
    return this.mutate(CreatCartMutation, { input })
  }

  getCart(id: string): Promise<any> {
    return this.query(CartQuery, { id })
  }

  cartLinesAdd(input: CartLinesAddInput): Promise<any> {
    return this.mutate(CartLinesAddMutation, input)
  }

  cartLinesRemove(input: CartLinesUpdateInput): Promise<any> {
    return this.mutate(CartLinesRemoveMutation, input)
  }

  cartLinesUpdate(input: CartLinesUpdateInput): Promise<any> {
    return this.mutate(CartLinesUpdateMutation, input)
  }

  cartBuyerIdentityUpdate(buyerIdentity: any, cartId: string): Promise<any> {
    return this.mutate(CartBuyerIdentityUpdateMutation, {
      buyerIdentity,
      cartId,
    })
  }

  cartDiscountCodesUpdate(
    cartId: string,
    discountCodes: string[]
  ): Promise<any> {
    return this.mutate(CartDiscountCodesUpdateMutation, {
      cartId,
      discountCodes,
    })
  }
  cartAttributesUpdate(attributes: any[], cartId: string): Promise<any> {
    return this.mutate(CartAttributesUpdateMutation, {
      attributes,
      cartId,
    })
  }

  createCheckout(input: CreateCheckoutInput): Promise<any> {
    return this.mutate(CreateCheckoutMutation, { input })
  }

  getCheckout(id: string): Promise<any> {
    return this.query(CheckoutQuery, { id })
  }

  checkoutLineItemsAdd(input: CheckoutLineItemsAddInput): Promise<any> {
    return this.mutate(CheckoutLineItemsAddMutation, input)
  }

  checkoutLineItemsRemove(input: CheckoutLineItemsRemoveInput): Promise<any> {
    return this.mutate(CheckoutLineItemsRemoveMutation, input)
  }

  checkoutLineItemsUpdate(input: CheckoutLineItemsUpdateInput): Promise<any> {
    return this.mutate(CheckoutLineItemsUpdateMutation, input)
  }

  checkoutAttributesUpdate(input: CheckoutAttributesUpdateInput): Promise<any> {
    return this.mutate(CheckoutAttributesUpdateV2, input)
  }

  checkoutDiscountCodeApply(
    input: CheckoutDiscountCodeApplyInput
  ): Promise<any> {
    return this.mutate(CheckoutDiscountCodeApplyV2, input)
  }

  checkoutDiscountCodeRemove(
    input: CheckoutDiscountCodeRemoveInput
  ): Promise<any> {
    return this.mutate(CheckoutDiscountCodeRemove, input)
  }

  checkoutEmailUpdate(input: CheckoutEmailUpdateInput): Promise<any> {
    return this.mutate(CheckoutEmailUpdateV2, input)
  }

  updateCheckoutShippingAddress(
    checkoutId: string,
    shippingAddress: MailingAddressInput
  ): Promise<any> {
    return this.mutate(CheckoutShippingAddressUpdateV2, {
      checkoutId,
      shippingAddress,
    })
  }

  updateCheckoutShippingLine(
    checkoutId: string,
    shippingRateHandle: string
  ): Promise<any> {
    return this.mutate(CheckoutShippingLineUpdate, {
      checkoutId,
      shippingRateHandle,
    })
  }

  completeCheckout(
    checkoutId: string,
    payment: TokenizedPaymentInput
  ): Promise<any> {
    return this.mutate(CheckoutCompleteWithTokenizedPaymentV3, {
      checkoutId,
      payment,
    })
  }

  completeFreeCheckout(checkoutId: string): Promise<any> {
    return this.mutate(CheckoutCompleteFree, { checkoutId })
  }

  getURLRedirects(path: string): Promise<any> {
    return this.query(UrlRedirectQuery, { query: `path:${path}` })
  }

  createCustomer(input: CustomerCreateInput): Promise<any> {
    return this.mutate(CreateCustomerMutation, { input })
  }

  createCustomerAccessToken(
    input: CustomerAccessTokenCreateInput
  ): Promise<any> {
    return this.mutate(CreateCustomerAccessTokenMutation, { input })
  }

  getCustomer(customerAccessToken: string): Promise<any> {
    return this.query(CustomerQuery, {
      customerAccessToken,
    })
  }

  recoverCustomer(email: string): Promise<any> {
    return this.mutate(CustomerRecoverMutation, {
      email,
    })
  }

  resetCustomer(id: string, input: CustomerResetInput): Promise<any> {
    return this.mutate(CustomerResetMutation, {
      id,
      input,
    })
  }

  activeCustomer(id: string, input: CustomerActivateInput): Promise<any> {
    return this.mutate(CustomerActiveMutation, {
      id,
      input,
    })
  }

  createCustomerAddress(
    address: MailingAddressInput,
    customerAccessToken: string
  ): Promise<any> {
    return this.mutate(CreateCustomerAddressMutation, {
      address,
      customerAccessToken,
    })
  }

  updateCustomerAddress(
    address: MailingAddressInput,
    customerAccessToken: string,
    id: string
  ): Promise<any> {
    return this.mutate(UpdateCustomerAddressMutation, {
      address,
      customerAccessToken,
      id,
    })
  }

  getLocalization(): Promise<any> {
    return this.query(LocalizationQuery)
  }

  getProductRecommendations(id: string): Promise<any> {
    return this.query(ProductRecommendations, { id })
  }
}

/**
 * Remove edges, node and __typename from graphql response
 *
 * @param {Object} input - The graphql response
 * @returns {Object} Clean graphql response
 */
const cleanGraphQLResponse = function (input: any) {
  if (!input) return null
  if (isPrimitiveType(input)) return input

  const output = {} as any
  Object.keys(input).forEach((key) => {
    if (key === 'nodes') {
      output[key] = Object.values(cleanGraphQLResponse(input[key]))
    } else if (input[key] && input[key].edges) {
      output[key] = input[key].edges.map((edge: any) => {
        if (edge.cursor) {
          return cleanGraphQLResponse(
            Object.assign({}, edge.node, { __cursor: edge.cursor })
          )
        }
        return cleanGraphQLResponse(edge.node)
      })
      if (input[key].pageInfo) {
        output[`__${key}__pageInfo`] = input[key].pageInfo
      }
      if (input[key].filters) {
        output[`__${key}__filters`] = input[key].filters
      }
    } else if (isObjectType(input[key])) {
      output[key] = cleanGraphQLResponse(input[key])
    } else if (key !== '__typename') {
      output[key] = input[key]
    }
  })

  return output
}
